// ─── suggestFeedbackReply ────────────────────────────────────────────────
// 2026-07-21 — Pré-rédaction IA d'une réponse dans le flux Suivi (demande
// Benoit, calquée sur le réflexe Benfitcoach). L'IA propose un brouillon
// dans la voix d'Anissa, TOUJOURS éditable, JAMAIS envoyé automatiquement :
// le brouillon remplit la zone de texte, Anissa relit, ajuste, envoie.
//
// Périmètre STRICTEMENT relationnel : accuser réception, encourager,
// refléter ce que la cliente a partagé (ressenti, photos). AUCUN contenu
// clinique : pas de conseil nouveau, pas de complément, pas de dosage,
// pas d'interprétation de symptôme. Si un ajustement semble utile, le
// brouillon renvoie vers la prochaine consultation. Fail-closed : erreur
// IA → throw, la zone de texte reste intacte.
//
// Passe par callClaude (services/anthropic.js) : garde de vocabulaire
// compliance injectée à la source, clé API côté serveur (api/claude).

import { callClaude } from "./anthropic";

const SYSTEM = `Tu rédiges un BROUILLON de message court qu'Anissa Deroubaix, nutritionniste, enverra à sa cliente depuis son espace de suivi. Anissa relira et modifiera ce brouillon avant tout envoi.

VOIX D'ANISSA (obligatoire) :
- Tutoiement, chaleureuse mais directe, jamais professorale ni culpabilisante.
- Oral naturel, phrases courtes. Zéro emoji, zéro jargon, zéro formule de brochure.

PÉRIMÈTRE STRICT (règles absolues, priorité sur tout le reste) :
- Message RELATIONNEL uniquement : accuser réception, remercier, encourager, refléter ce que la cliente a partagé.
- INTERDIT : tout conseil nutritionnel nouveau, toute modification du programme, tout complément ou dosage, toute interprétation de symptôme, tout diagnostic, toute promesse de résultat.
- Si les éléments partagés suggèrent qu'un ajustement serait utile : écrire simplement qu'Anissa regarde ça de près et qu'elles en reparleront à la prochaine consultation — RIEN de plus.
- Ne jamais qualifier un poids ou un corps (ni en bien, ni en mal). Ne jamais commenter négativement un aliment photographié.
- Ne JAMAIS répéter un chiffre dans le message (poids, calories, grammes, pourcentages) : les données chiffrées du contexte servent uniquement à comprendre la situation, jamais à être citées à la cliente.
- 2 à 4 phrases maximum. Pas de signature, pas de titre.

Réponds UNIQUEMENT avec le texte du message, rien d'autre.`;

// 2026-07-21 (validation Anissa) : pour les profils sensibles (sans-chiffres,
// TCA, ou fiche introuvable = fail-closed), aucun compliment ni jugement sur
// les aliments photographiés — le renforcement positif est aussi un risque
// pour ces profils. Observations neutres et bienveillantes uniquement.
const SENSIBLE_ADDENDUM = `

PROFIL SENSIBLE (règle supplémentaire absolue) :
- Aucun compliment ni jugement sur les aliments ou les repas photographiés — ni positif, ni négatif. Pas de « bien », « bon choix », « équilibré », « sain ».
- Uniquement des observations neutres et bienveillantes : remercier pour le partage, refléter sans évaluer.`;

/**
 * @param {object} params
 * @param {string} params.clientFirstName - prénom de la cliente
 * @param {string} params.context - description factuelle de ce qu'elle a partagé
 * @param {boolean} [params.profilSensible] - sans-chiffres/TCA/fiche introuvable :
 *   interdit tout jugement (même positif) sur les aliments photographiés
 * @returns {Promise<string>} brouillon (texte brut, ≤ ~900 caractères)
 */
export async function suggestFeedbackReply({ clientFirstName, context, profilSensible }) {
  const user = [
    `Cliente : ${clientFirstName || "la cliente"}.`,
    "",
    "Ce qu'elle vient de partager dans son app :",
    String(context || "").slice(0, 1200),
    "",
    "Rédige le brouillon de réponse.",
  ].join("\n");

  const system = profilSensible ? SYSTEM + SENSIBLE_ADDENDUM : SYSTEM;
  const text = await callClaude({ system, user, maxTokens: 300 });
  const cleaned = String(text || "").trim();
  if (!cleaned) throw new Error("Brouillon vide — réessaie");
  return cleaned.slice(0, 900);
}
